import { GetStageDeploymentEventsResponseEvents } from "@microtica/ms-engine-sdk";
import { Button, Card, Checkbox, Col, Modal, ModalFuncProps, notification, Row, Skeleton, Space, Tooltip } from "antd";
import {
    CheckCircleFilled,
    UserOutlined,
    ClockCircleOutlined,
    ExpandAltOutlined,
    InfoCircleOutlined,
    ShrinkOutlined
} from "@ant-design/icons";
import moment from "moment-timezone";
import { Fragment, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { getEnvironmentService, getElasticSearchService } from "../../backend";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import Heading from "../../components/Heading";
import { EnvDeployment, IEnvDetails } from "../../types";
import { mapInfraDeployments } from "../../utils/deployments";
import { trackCancelDeployment } from "../../backend/tracking/deployment";
import Logs from "../../components/Logs";
import EnvironmentDeploymentResource from "../../components/EnvironmentDeploymentResource";

const EnvironmentInfraDeploymentDetails = () => {
    const { projectId, envId, deploymentId } = useParams();
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [events, setEvents] = useState<GetStageDeploymentEventsResponseEvents[]>([]);
    const [loading, setLoading] = useState(true);
    const [deploymentDetails, setDeploymentDetails] = useState<EnvDeployment>();
    const [disableCancelDeploymentBtn, setDisableCancelDeploymentBtn] = useState(false);
    const [eventsExpanded, setEventsExpanded] = useState(false);
    const [showUpdatesOnly, setShowUpdatesOnly] = useState(false);
    const [hasUpdates, setHasUpdates] = useState(false);
    const [, setModal] = useState<{
        destroy: () => void;
        update: (configUpdate: ModalFuncProps) => void;
    }>();

    useEffect(() => {

        (async () => {
            const data = await loadData();
            const hasUpdates = data?.resources.some(r => !r.prev.equal);
            setHasUpdates(hasUpdates || false);
            setShowUpdatesOnly(hasUpdates || false);
            setEventsExpanded(data?.resources.length === 0);
        })();

        moment.updateLocale(moment.locale(), { invalidDate: "" });

        const interval = setInterval(async () => {
            const data = await loadData();

            // Stop getting updates if the deployment is already completed
            if (data?.status.name.endsWith("COMPLETE")) {
                clearInterval(interval);
            }
        }, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            const [
                { data: { events: cfnEvents } },
                { data: { response: deployments } }
            ] = await Promise.all([
                getEnvironmentService().getStageDeploymentEvents(envId!, projectId!).catch(_ => ({ data: { events: [] } })),
                getElasticSearchService().getStageDeploymentHistory(projectId!, envId!)
            ]);

            const deployment = deployments.find(d => d.deploymentId === deploymentId);
            const infraDeployment = mapInfraDeployments(deployments, envDetails.lastDeploymentId).find(d => d.id === deploymentId);
            setDeploymentDetails(infraDeployment);

            // Get the deployment events from the history
            // If not present, get the events from CFN directly
            const historyEvents = [
                ...(infraDeployment?.status.errors || []).map(e => ({
                    eventId: `${e.logicalResourceId}-${e.statusReason}`,
                    logicalResourceId: e.logicalResourceId,
                    resourceStatus: "DEPLOYMENT_FAILED",
                    resourceType: e.resourceType,
                    statusReason: e.statusReason,
                    timestamp: new Date(infraDeployment?.timestamp!).getTime(),
                    stackId: "",
                    stackName: "",
                    physicalResourceId: "",
                    clientRequestToken: ""
                })),
                ...deployment?.logs?.events[`environment-${envDetails.id.split("-")[0]}`].events || [],
            ];

            const deploymentEvents = deployment?.status === "DEPLOY_QUEUED" ? [] :
                historyEvents.length > 0 ? historyEvents : cfnEvents;
            const sortedEvents = deploymentEvents.sort((d1, d2) => Number(new Date(d1.timestamp)) - Number(new Date(d2.timestamp)));
            setEvents(sortedEvents);

            return infraDeployment;
        } catch (error) {
            console.log("ERROR", error);
        } finally {
            setLoading(false);
        }
    }

    const showCancelStageDeploymentConfirm = () => {
        const modal = Modal.confirm({
            title: "Cancel deployment",
            content:
                <div>
                    Are you sure you want to cancel the deployment? This action cannot be undone.
                </div>,
            okText: "Yes",
            cancelText: "No",
            centered: true,
            async onOk() {
                return cancelStageDeployment();
            },
            onCancel() {
                setModal(undefined);
            }
        });
        setModal(modal);
    }

    const cancelStageDeployment = async () => {
        try {
            setDisableCancelDeploymentBtn(true);
            await getEnvironmentService().cancelDeploy(envId!, projectId!);
            notification.info({
                message: "Deployment canceled",
                description: "The environment deployment has been canceled"
            });
            trackCancelDeployment(envId!);
        } catch (error: any) {
            setDisableCancelDeploymentBtn(false);
            console.log(error);
            notification.info({
                message: "Deployment canceled error",
                description: error.response?.data?.message || "An unknown erorr has occured"
            });
        }
        setModal(undefined);
    }

    const PageTitle = () => (
        <div className="flex-justify-space-between">
            <Space>
                {
                    deploymentDetails?.active &&
                    <Tooltip title="The deployment that is available to your end users.">
                        <CheckCircleFilled style={{
                            fontSize: 24,
                            color: "green"
                        }} />
                    </Tooltip>
                }
                <div>{deploymentDetails?.id}</div>
                <DeploymentStatusTag size="large" status={deploymentDetails?.status.name!} />
            </Space>
            {
                ["CREATE_IN_PROGRESS", "UPDATE_IN_PROGRESS"].includes(envDetails!.status!) &&
                <Button
                    disabled={disableCancelDeploymentBtn}
                    danger={true}
                    onClick={showCancelStageDeploymentConfirm}
                >
                    Cancel deployment
                </Button>
            }
        </div>
    )

    const PageDescription = () => (
        <Space direction="vertical">
            INFRASTRUCTURE
            <Space>
                {/* <ThunderboltOutlined /> */}
                <Space>
                    <span>
                        <UserOutlined /> {deploymentDetails?.createdBy}
                    </span>
                    <span>
                        <ClockCircleOutlined /> {deploymentDetails?.createdAt}
                    </span>
                </Space>
            </Space>
        </Space>
    )

    return (
        loading ?
            <Skeleton /> :
            <>
                <Heading
                    title={<PageTitle />}
                    description={<PageDescription />}
                    level={3}
                />
                <Row gutter={24}>
                    {/* Events */}
                    <Col span={eventsExpanded ? 24 : 16}>
                        <Logs
                            title="Deployments Logs"
                            extra={
                                deploymentDetails?.resources.length! > 0 &&
                                <Button
                                    type="link"
                                    style={{ paddingRight: 0, color: "white" }}
                                    icon={eventsExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                                    onClick={() => setEventsExpanded(!eventsExpanded)}
                                >
                                    {eventsExpanded ? "Show resources" : "Expand"}
                                </Button>
                            }>
                            {
                                deploymentDetails?.error ?
                                    <div style={{ color: "var(--error-color)" }}>
                                        [DEPLOYMENT_FAILED] {deploymentDetails.error}
                                    </div>
                                    :
                                    events.map(e =>
                                        <Fragment key={e.eventId}>
                                            <div>
                                                <Space>
                                                    <span style={{ color: "#097BB5" }}>{moment(e.timestamp).format()}</span>
                                                    <span style={{ color: e.resourceStatus?.includes("FAILED") ? "var(--error-color)" : "white" }}>
                                                        [{e.resourceStatus}]
                                                    </span>
                                                    {e.logicalResourceId}
                                                </Space>
                                            </div>
                                            {
                                                e.statusReason &&
                                                <div style={{ color: e.resourceStatus?.includes("FAILED") ? "var(--error-color)" : "white" }}>
                                                    {`===> ${e.statusReason}`}
                                                </div>
                                            }
                                        </Fragment>
                                    )
                            }
                        </Logs>
                    </Col>

                    {/* Updated resources */}
                    {
                        !eventsExpanded &&
                        <Col span={8} style={{
                            border: "1px solid var(--border-color-base)",
                            borderRadius: "var(--border-radius-base)",
                        }}>
                            <Heading
                                title={
                                    <div className="flex-justify-space-between flex-align-center">
                                        Deployment Resources
                                    </div>
                                }
                                description={
                                    <Tooltip title="Show only the resources that has version or configuration changes in this deployment.">
                                        <Card size="small" type="inner" className="no-bg">
                                            <Checkbox
                                                checked={showUpdatesOnly}
                                                onChange={(e) => setShowUpdatesOnly(e.target.checked)}
                                            >
                                                Only updated resources
                                            </Checkbox>
                                            <InfoCircleOutlined />
                                        </Card>
                                    </Tooltip>
                                }
                                level={5}
                            />
                            {
                                deploymentDetails?.resources
                                    .filter(r => !showUpdatesOnly ? true : !r.prev?.equal)
                                    .map(r => (<EnvironmentDeploymentResource deploymentType={deploymentDetails.type} resource={r} key={r.id} />))
                            }
                            {
                                !hasUpdates && showUpdatesOnly &&
                                <div className="text-center">
                                    No resources with updated version or configuration changes in this deployment.
                                </div>
                            }
                        </Col>
                    }
                </Row>
            </>
    )
}

export default EnvironmentInfraDeploymentDetails;